import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  BarChart3,
  CreditCard,
  Target,
  Wallet,
  Settings,
  LogOut,
  Eye,
  EyeOff,
  PieChart,
  PanelLeftClose,
  PanelLeft,
  CalendarDays,
  Shield,
  Bot,
  Sun,
  Moon,
  MoreHorizontal,
  MessageCircle,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useFeatureFlags, type FeatureKey } from '@/contexts/FeatureFlagContext';
import { CleviaLogo } from '@/components/CleviaLogo';
import { useCleviaFeedback } from '@/contexts';

interface SidebarStore {
  collapsed: boolean;
  toggleCollapsed: () => void;
}

export const useSidebar = create<SidebarStore>()(
  persist(
    (set) => ({
      collapsed: false,
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
    }),
    { name: 'clevia-sidebar' }
  )
);

interface SidebarProps {
  activePage?: string;
}

export function Sidebar({ activePage }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { logout, isAdmin } = useAuth();
  const { isSecretMode, toggleSecretMode } = useCurrency();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { isEnabled } = useFeatureFlags();
  const { openFeedback } = useCleviaFeedback();
  const { collapsed, toggleCollapsed } = useSidebar();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMenuEnter = () => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    setIsMenuOpen(true);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => setIsMenuOpen(false), 200);
  };

  // Derive active page from URL if not explicitly passed
  const currentPage =
    activePage ??
    (() => {
      const path = location.pathname;
      if (path.startsWith('/accounts')) return 'accounts';
      if (path.startsWith('/transactions')) return 'transactions';
      if (path.startsWith('/budgets')) return 'budgets';
      if (path.startsWith('/goals')) return 'goals';
      if (path.startsWith('/coach')) return 'coach';
      if (path.startsWith('/calendar')) return 'calendar';
      if (path.startsWith('/settings')) return 'settings';
      if (path.startsWith('/dashboard')) return 'dashboard';
      return 'dashboard';
    })();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    {
      id: 'dashboard',
      icon: BarChart3,
      label: t('dashboard.nav.dashboard'),
      path: '/dashboard',
      featureKey: 'dashboard' as FeatureKey,
    },
    {
      id: 'accounts',
      icon: Wallet,
      label: t('dashboard.nav.accounts'),
      path: '/accounts',
      featureKey: 'accounts' as FeatureKey,
    },
    {
      id: 'transactions',
      icon: CreditCard,
      label: t('dashboard.nav.transactions'),
      path: '/transactions',
      featureKey: 'transactions' as FeatureKey,
    },
    {
      id: 'budgets',
      icon: PieChart,
      label: t('dashboard.nav.budgets'),
      path: '/budgets',
      featureKey: 'budgets' as FeatureKey,
    },
    {
      id: 'calendar',
      icon: CalendarDays,
      label: t('dashboard.nav.calendar' as any),
      path: '/calendar',
      featureKey: 'calendar' as FeatureKey,
    },
    {
      id: 'goals',
      icon: Target,
      label: t('dashboard.nav.goals'),
      path: '/goals',
      featureKey: 'goals' as FeatureKey,
    },
    {
      id: 'coach',
      icon: Bot,
      label: t('dashboard.nav.coach'),
      path: '/coach',
      featureKey: undefined,
      badge: isEnabled('ai_chat') ? undefined : t('common.comingSoon'),
    },
  ].filter((item: any) => {
    if (item.featureKey === undefined) return true;
    return isEnabled(item.featureKey);
  });

  return (
    <aside
      style={{ top: 0, height: '100%' }}
      className={`fixed left-0 ${collapsed ? 'w-[72px]' : 'w-64'} bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 z-50 hidden lg:flex flex-col transition-all duration-200`}
    >
      {/* Logo */}
      <div
        className={`${collapsed ? 'px-3 py-[26px]' : 'px-6'} py-[27px] border-b border-gray-50 dark:border-gray-800`}
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {collapsed ? (
            <button
              onClick={toggleCollapsed}
              className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title={t('sidebar.expand' as any)}
            >
              <PanelLeft className="w-[18px] h-[18px]" strokeWidth={2} />
            </button>
          ) : (
            <>
              <div className="cursor-pointer group" onClick={() => navigate('/dashboard')}>
                <CleviaLogo
                  size="sm"
                  className="group-hover:scale-105 transition-transform duration-200"
                />
              </div>
              <button
                onClick={toggleCollapsed}
                className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                title={t('sidebar.collapse' as any)}
              >
                <PanelLeftClose className="w-4 h-4" strokeWidth={2} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === currentPage;

          return (
            <button
              key={item.id}
              onClick={() => item.path !== '#' && navigate(item.path)}
              className={`relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-[#16c47f]/[0.08] text-[#16c47f]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/80'
              }`}
              title={collapsed ? item.label : undefined}
            >
              {isActive && !collapsed && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-[#16c47f] rounded-r-full" />
              )}
              <div className="relative flex-shrink-0">
                <Icon
                  className={`w-[18px] h-[18px] transition-colors ${
                    isActive ? 'text-[#16c47f]' : 'text-gray-400 dark:text-gray-500'
                  }`}
                  strokeWidth={isActive ? 2.5 : 2}
                />
              </div>
              {!collapsed && (
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <span className="font-medium whitespace-nowrap">{item.label}</span>
                  {item.badge && (
                    <span className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-500 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom actions */}
      <div className="px-3 pb-5 pt-3 border-t border-gray-50 dark:border-gray-800 space-y-0.5">
        {/* Hide amounts toggle */}
        <button
          onClick={toggleSecretMode}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium transition-all duration-150 ${
            isSecretMode
              ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-700/50'
          }`}
          title={
            collapsed
              ? isSecretMode
                ? t('sidebar.showAmounts')
                : t('sidebar.hideAmounts')
              : undefined
          }
        >
          {isSecretMode ? (
            <EyeOff className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          ) : (
            <Eye className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          )}
          {!collapsed && (
            <span>{isSecretMode ? t('sidebar.showAmounts') : t('sidebar.hideAmounts')}</span>
          )}
        </button>
        {/* More menu */}
        <div className="relative" onMouseEnter={handleMenuEnter} onMouseLeave={handleMenuLeave}>
          <button
            className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-700/50 transition-all duration-150`}
            title={collapsed ? 'Menu' : undefined}
          >
            <MoreHorizontal
              className="w-[18px] h-[18px] text-gray-400 dark:text-gray-500 flex-shrink-0"
              strokeWidth={2}
            />
            {!collapsed && <span>Plus</span>}
          </button>

          {/* Floating panel */}
          <div
            className={`absolute ${collapsed ? 'left-full bottom-0 ml-2' : 'bottom-full left-0 mb-1.5 w-full'} min-w-[200px] bg-white dark:bg-gray-800 rounded-xl shadow-lg shadow-black/5 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 py-1.5 z-[60] transition-all duration-150 ${collapsed ? 'origin-bottom-left' : 'origin-bottom'} ${
              isMenuOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            }`}
          >
            {/* Settings */}
            <button
              onClick={() => {
                navigate('/settings');
                setIsMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <Settings className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              <span>{t('dashboard.nav.settings')}</span>
            </button>

            {/* Sentry feedback */}
            <button
              onClick={() => {
                setIsMenuOpen(false);
                openFeedback('support');
              }}
              className="w-full flex items-center gap-3 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-[#16c47f] dark:hover:text-[#16c47f] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <MessageCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              <span>{t('support.contactOrSuggest' as any)}</span>
            </button>

            {/* Admin */}
            {isAdmin && (
              <button
                onClick={() => {
                  navigate('/admin');
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50/50 dark:hover:bg-violet-900/20 transition-colors"
              >
                <Shield className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span>Admin</span>
              </button>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="w-full flex items-center gap-3 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              {isDarkMode ? (
                <Sun className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              ) : (
                <Moon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              )}
              <span>{isDarkMode ? 'Light mode' : 'Dark mode'}</span>
              <div
                className={`ml-auto w-8 h-[18px] rounded-full transition-colors ${
                  isDarkMode ? 'bg-[#16c47f]' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`relative top-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform -translate-y-1/2 ${
                    isDarkMode ? 'translate-x-[16px]' : 'translate-x-[2px]'
                  }`}
                />
              </div>
            </button>

            {/* Separator */}
            <div className="my-1 mx-3 border-t border-gray-100 dark:border-gray-700/50" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3.5 py-2 text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              <span>{t('dashboard.nav.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
